import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { updateActiveWIRStep, updateSelectedSection } from 'redux/WellGuardian';
import { useActiveWIRStep, useCountryState, useSelectedWIR } from 'redux/WellGuardian/selectors';
import { WIR } from 'types/wellGuardian';

import { getActivePhaseFromWirState, getInterventionCompleteIndex, getPhases } from '../utils';
import { getNonReactiveSections, getReactiveSections } from '../utils/getSectionByCountry';
import { getInterventionOperationSectionKey } from '../utils/getSectionData';
import { Phase, Section } from '../wir.constants';

export const useProcessSection = (section: string) => {
  const wirFromState = useSelectedWIR();
  const dispatch = useDispatch();
  const countryState = useCountryState();
  const activeStep = useActiveWIRStep();

  const interOperationSectionKey = getInterventionOperationSectionKey(countryState);

  const allSections = useMemo(
    () =>
      wirFromState?.isUnplannedReactiveScope
        ? getReactiveSections(wirFromState, countryState)
        : getNonReactiveSections(wirFromState, countryState),
    [countryState, wirFromState],
  );

  const openedSection = useMemo(
    () => allSections.find(item => item.label === section),
    [allSections, section],
  );

  const openPhaseSections = useMemo(
    () => allSections.filter(item => item.phase === openedSection?.phase),
    [allSections, openedSection?.phase],
  );

  const handleInterventionStep = useCallback(
    (wirResponse: WIR) => {
      // Go to next phase as coming from BE which should be next one
      const phaseIndex = getActivePhaseFromWirState(wirResponse, countryState);

      dispatch(updateActiveWIRStep(phaseIndex));
      if (phaseIndex !== getInterventionCompleteIndex(wirResponse, countryState)) {
        const nextPhase = getPhases(wirResponse, countryState)[phaseIndex];
        if (
          nextPhase !== interOperationSectionKey ||
          (nextPhase === interOperationSectionKey &&
            wirResponse.phases?.[getInterventionOperationSectionKey(countryState)]?.dateStarted)
        ) {
          const firstSectionOfNextPhase = allSections.filter(item => item.phase === nextPhase)[0];
          dispatch(updateSelectedSection(firstSectionOfNextPhase.label));
        }
      }
    },
    [allSections, countryState, dispatch, interOperationSectionKey],
  );

  const handleNextStep = useCallback(() => {
    // Sub task is completed and confirmed to go to next subtask
    const sectionIndex = openPhaseSections.findIndex(item => item.label === section);
    // Navigate to next section if not last section of the active phase
    if (sectionIndex !== openPhaseSections.length - 1) {
      dispatch(updateSelectedSection(openPhaseSections[sectionIndex + 1].label));
    }
  }, [dispatch, openPhaseSections, section]);

  const processSection = useCallback(
    (wirResponse: WIR) => {
      const activePhase = getPhases(wirResponse, countryState)[activeStep];
      const openSectionCompleteConfirmation =
        wirFromState?.phases?.[openedSection?.phase as Phase]?.sections?.[
          openedSection?.value as Section
        ]?.sectionCompleteConfirmation;
      const openSectionCompleteConfirmationContinue =
        Boolean(openSectionCompleteConfirmation) === openSectionCompleteConfirmation
          ? openSectionCompleteConfirmation
          : false;
      const openPhaseSections = allSections.filter(item => item.phase === openedSection?.phase);
      const openPhaseIsAllTicked = !openPhaseSections.some(
        item =>
          !wirResponse?.phases?.[openedSection?.phase as Phase]?.sections?.[item.value as Section]
            ?.isTicked,
      );

      const goToNextSection =
        (activePhase === openedSection?.phase && openSectionCompleteConfirmationContinue) ||
        (activePhase === openedSection?.phase && openPhaseIsAllTicked);

      if (goToNextSection) {
        if (openPhaseIsAllTicked) {
          handleInterventionStep(wirResponse);
        } else {
          handleNextStep();
        }
      }
    },
    [
      countryState,
      activeStep,
      wirFromState?.phases,
      openedSection?.phase,
      openedSection?.value,
      allSections,
      handleInterventionStep,
      handleNextStep,
    ],
  );

  return {
    processSection,
  };
};
